import React from "react";
import { Box, Container, Typography, Button } from "@mui/material";
import { motion } from "framer-motion";
import { Link as RouterLink } from "react-router-dom";

import accountImage from "../../static/media/Home/Account.webp";

const PRIMARY = "#06BBC7";
const DARK_PRIMARY = "#047b84";
const YELLOW = "#F7C948";

const MotionBox = motion(Box);

const Account = () => {
  return (
    <Box
      component="section"
      sx={{
        py: { xs: 5, md: 8 },
        bgcolor: "#ffffff",
        overflow: "hidden",
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            background: `linear-gradient(135deg, ${DARK_PRIMARY} 0%, ${PRIMARY} 58%, #08cbd8 100%)`,
            borderRadius: { xs: "28px", md: "38px" },
            overflow: "hidden",
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "1fr 0.95fr",
            },
            alignItems: "center",
            minHeight: { md: 500 },
          }}
        >
          <MotionBox
            initial={{ opacity: 0, x: 28, scale: 0.98 }}
            whileInView={{ opacity: 1, x: 0, scale: 1 }}
            viewport={{ once: true, amount: 0.25 }}
            transition={{ duration: 0.65, ease: "easeOut" }}
            sx={{
              order: { xs: 1, md: 2 },
              height: { xs: 390, sm: 480, md: "100%" },
              minHeight: { md: 500 },
              position: "relative",
              overflow: "hidden",
            }}
          >
            <Box
              component="img"
              src={accountImage}
              alt="Lily's Sunflowers registro de clientes"
              loading="lazy"
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                objectPosition: "center",
                display: "block",
              }}
            />

            <Box
              sx={{
                position: "absolute",
                inset: 0,
                background: {
                  xs: `linear-gradient(180deg, rgba(4, 123, 132, 0.03) 0%, rgba(4, 123, 132, 0.28) 100%)`,
                  md: `linear-gradient(90deg, rgba(4, 123, 132, 0.55) 0%, rgba(6, 187, 199, 0.08) 48%, rgba(6, 187, 199, 0.02) 100%)`,
                },
                pointerEvents: "none",
              }}
            />
          </MotionBox>

          <MotionBox
            initial={{ opacity: 0, x: -28 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.25 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            sx={{
              order: { xs: 2, md: 1 },
              p: { xs: 3, sm: 4.5, md: 6 },
              color: "#ffffff",
              textAlign: { xs: "center", md: "left" },
            }}
          >
            <Typography
              component="h2"
              sx={{
                fontFamily: "'Playfair Display', serif",
                fontSize: { xs: "2.25rem", sm: "2.8rem", md: "3.5rem" },
                fontWeight: 500,
                lineHeight: 1.04,
                letterSpacing: "-0.04em",
                color: "#ffffff",
                mb: 2,
              }}
            >
              Regístrate y vive una experiencia más cercana
            </Typography>

            <Typography
              sx={{
                maxWidth: 570,
                mx: { xs: "auto", md: 0 },
                color: "rgba(255,255,255,0.9)",
                fontSize: { xs: "0.98rem", md: "1.08rem" },
                lineHeight: 1.75,
                fontWeight: 400,
                mb: 3,
              }}
            >
              Crea tu cuenta para agendar tus citas, revisar tus avances,
              recibir atención personalizada y mantener el control de tus
              tratamientos faciales, corporales, capilares y de belleza.
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
                gap: 1.2,
                maxWidth: 560,
                mx: { xs: "auto", md: 0 },
                mb: 3.5,
              }}
            >
              {[
                "Agenda tus citas fácilmente",
                "Revisa el progreso de tus tratamientos",
                "Recibe atención personalizada",
                "Accede a beneficios y novedades",
              ].map((item, index) => (
                <MotionBox
                  key={item}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.25 }}
                  transition={{
                    duration: 0.38,
                    delay: index * 0.06,
                    ease: "easeOut",
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    justifyContent: { xs: "center", sm: "flex-start" },
                    color: "#ffffff",
                    fontSize: { xs: "0.9rem", md: "0.95rem" },
                    fontWeight: 600,
                    lineHeight: 1.35,
                  }}
                >
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      bgcolor: index % 2 === 0 ? YELLOW : "#ffffff",
                      flexShrink: 0,
                    }}
                  />

                  <Typography
                    component="span"
                    sx={{
                      fontSize: "inherit",
                      fontWeight: "inherit",
                      lineHeight: "inherit",
                    }}
                  >
                    {item}
                  </Typography>
                </MotionBox>
              ))}
            </Box>

            <Button
              component={RouterLink}
              to="/registro"
              variant="contained"
              sx={{
                bgcolor: YELLOW,
                color: "#1f2937",
                px: 4,
                py: 1.18,
                borderRadius: "999px",
                textTransform: "none",
                fontWeight: 700,
                fontSize: "0.95rem",
                boxShadow: "none",
                "&:hover": {
                  bgcolor: "#efbd35",
                  boxShadow: "0 14px 28px rgba(247, 201, 72, 0.28)",
                },
              }}
            >
              Registrarme
            </Button>
          </MotionBox>
        </Box>
      </Container>
    </Box>
  );
};

export default Account;